import Offer from "../OfferClass/Offer/Offer";
import LoyaltyOffer from "../LoyaltyOfferClass/LoyaltyOffer";
import {
  PromoActionType,
  PromoBannerItem,
  PromoContentType,
  PromoImageContent,
  PromoTextContent,
} from "./promoMaterialTypes";

/**
 * Which card a banner became. An enum rather than bare literals because the
 * discriminant is written in five places (these types, the four construction
 * sites below, and the rail's switch) — a typo in any one of them is a card
 * that silently never renders.
 */
export enum PromoBannerKind {
  OFFER = "offer",
  ANNOUNCEMENT = "announcement",
  IMAGE = "image",
  NOTHING = "nothing",
}

/**
 * Which card a banner becomes — decided here, in one pure function, so the rail
 * itself stays a list and the mapping can be tested without React or Redux.
 *
 * The rail is server-ordered and mixed: the customer's applicable offers and the
 * chain's admin-authored banners arrive interleaved in one list, and only their
 * shape says which is which.
 */
export type PromoBannerCardSpec =
  | PromoOfferBannerSpec
  | PromoAnnouncementBannerSpec
  | PromoImageBannerSpec
  | PromoBannerNothing;

/**
 * An offer the customer holds. The offer object drives the card — its icon
 * motif, apply/remove state, expiry and unavailable chip all come from the
 * existing offer banner — while the server's copy overrides its words, since
 * that copy is the offer's own UI with the campaign's overrides already merged.
 */
type PromoOfferBannerSpec = {
  kind: PromoBannerKind.OFFER;
  id: string;
  offer: LoyaltyOffer;
  copy?: PromoTextContent;
};

/** Admin-authored words with no offer behind them. */
type PromoAnnouncementBannerSpec = {
  kind: PromoBannerKind.ANNOUNCEMENT;
  id: string;
  content: PromoTextContent;
  action: PromoBannerItem["action"];
};

/** Admin-authored artwork, tappable as a whole. */
type PromoImageBannerSpec = {
  kind: PromoBannerKind.IMAGE;
  id: string;
  content: PromoImageContent;
  action: PromoBannerItem["action"];
};

/** Not renderable. [reason] is for the log, never for the customer. */
export type PromoBannerNothing = {
  kind: PromoBannerKind.NOTHING;
  id: string;
  reason: string;
};

export const resolvePromoBanner = (
  banner: PromoBannerItem,
  offers: LoyaltyOffer[]
): PromoBannerCardSpec => {
  const { id, content, action } = banner;

  if (content?.type === PromoContentType.IMAGE) {
    // An artwork banner with no artwork is the one case where the card would be
    // an empty box rather than a degraded one.
    return content.imageUrl
      ? { kind: PromoBannerKind.IMAGE, id, content, action }
      : {
          kind: PromoBannerKind.NOTHING,
          id,
          reason: "IMAGE banner carried no imageUrl",
        };
  }

  if (content?.type !== PromoContentType.TEXT) {
    return {
      kind: PromoBannerKind.NOTHING,
      id,
      reason: "banner carried no renderable content",
    };
  }

  // An offer-led banner names its offer in the action. Resolving it against the
  // customer's own list is what lets the card carry the live apply/remove state;
  // an id that no longer resolves means the offer stopped applying between the
  // two calls, so the campaign's words stand alone as an announcement.
  if (action?.type === PromoActionType.APPLY_OFFER && action.offerId) {
    const offer = offers.find((candidate) => candidate.id === action.offerId);
    if (offer) {
      return { kind: PromoBannerKind.OFFER, id, offer, copy: content };
    }
  }

  // Nothing readable in the customer's language, and no `en` to fall back to:
  // the same rule the announcement *pop-up* applies, because the alternative is
  // an empty card in the rail. Checked after the offer branch — an offer-led
  // card draws its words from the offer when the campaign's copy is unusable.
  if (
    !Offer.getLocalizedValue(content.headline) &&
    !Offer.getLocalizedValue(content.description)
  ) {
    return {
      kind: PromoBannerKind.NOTHING,
      id,
      reason: "TEXT banner had nothing localized for this language",
    };
  }

  return { kind: PromoBannerKind.ANNOUNCEMENT, id, content, action };
};
